import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { clearAllCampaignCaches } from '../utils/cacheManager';
import type { Session } from '@supabase/supabase-js';

interface User {
  id: string;
  email: string;
  name: string;
  client_id: string;
  role: string;
  isDemo?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function extractUserFromSession(session: Session): User | null {
  const authUser = session.user;
  if (!authUser) return null;

  const appMeta = authUser.app_metadata || {};
  const userMeta = authUser.user_metadata || {};

  const clientId = appMeta.client_id;
  if (!clientId) return null;

  return {
    id: authUser.id,
    email: authUser.email || '',
    name: userMeta.full_name || authUser.email?.split('@')[0] || '',
    client_id: clientId,
    role: appMeta.role || 'member',
    isDemo: false,
  };
}

function persistUser(user: User | null) {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setUserAndPersist = useCallback((u: User | null) => {
    setUser(u);
    persistUser(u);
  }, []);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!mounted) return;

      if (session) {
        const extracted = extractUserFromSession(session);
        if (extracted) {
          setUserAndPersist(extracted);
        } else {
          setUserAndPersist(null);
        }
      } else {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try {
            const parsed = JSON.parse(savedUser);
            const isValidUserId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parsed.id);
            const isValidClientId = parsed.client_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parsed.client_id);
            if (isValidUserId && isValidClientId) {
              setUser(parsed);
            } else {
              localStorage.removeItem('user');
            }
          } catch {
            localStorage.removeItem('user');
          }
        }
      }

      if (mounted) setIsLoading(false);
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_OUT') {
        setUserAndPersist(null);
        return;
      }

      if (session) {
        const extracted = extractUserFromSession(session);
        if (extracted) {
          setUserAndPersist(extracted);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setUserAndPersist]);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase(),
      password,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (!data.session) {
      return { success: false, error: 'No session returned' };
    }

    const extracted = extractUserFromSession(data.session);
    if (!extracted) {
      await supabase.auth.signOut();
      return { success: false, error: 'Account not fully provisioned. Please contact support.' };
    }

    setUserAndPersist(extracted);
    return { success: true };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUserAndPersist(null);
    clearAllCampaignCaches();
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to send reset email' };
      }
      return { success: true };
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, resetPassword, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
